import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { BookStatusBadge } from '../components/BookProgressBar';
import { getMyGenaiJobs } from '../actions/genaiActions';


function MyGeneratedBooksScreen() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [downloadingJobId, setDownloadingJobId] = useState(null);
    const [reorderJobId, setReorderJobId] = useState(null);

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const genaiMyJobs = useSelector(state => state.genaiMyJobs);
    const { loading, error, jobs } = genaiMyJobs;

    useEffect(() => {
        if (!userInfo) {
            navigate('/login');
        } else {
            dispatch(getMyGenaiJobs());
        }
    }, [dispatch, navigate, userInfo]);

    const WAITING_STEPS = ['D_approve_story', 'H_approve_pages', 'J_approve_final'];

    const getActionButton = (job) => {
        const isWaiting = WAITING_STEPS.includes(job.current_step);
        if (job.status === 'completed') {
            return (
                <Link to={`/genai/job/${job.job_id}`}>
                    <Button variant='success' size='sm'>📖 {t('myBooks.viewBook')}</Button>
                </Link>
            );
        }
        if (isWaiting) {
            return (
                <Link to={`/genai/job/${job.job_id}`}>
                    <Button variant='warning' size='sm'>✏️ {t('myBooks.reviewNow')}</Button>
                </Link>
            );
        }
        return (
            <Link to={`/genai/job/${job.job_id}`}>
                <Button variant='outline-primary' size='sm'>{t('myBooks.viewDetails')}</Button>
            </Link>
        );
    };

    const handleOrderPrint = async (job) => {
        setReorderJobId(job.job_id);
        try {
            const raw = window.prompt('How many extra copies would you like to order? (1-20)', '1');
            if (raw === null) {
                return;
            }
            const copies = parseInt(raw, 10);
            if (!Number.isInteger(copies) || copies < 1 || copies > 20) {
                alert('Please enter a number between 1 and 20.');
                return;
            }

            const response = await fetch(`/api/genai/jobs/${job.job_id}/order-more-copies/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ copies, payment_method: 'PayPal' }),
            });

            const data = await response.json();
            if (!response.ok) {
                alert(data.error || 'Could not create reorder order');
                return;
            }

            navigate(`/order/${data.order_id}`);
        } finally {
            setReorderJobId(null);
        }
    };

    const handleDownloadPDF = async (jobId) => {
        setDownloadingJobId(jobId);
        try {
            // Make authenticated request to download PDF
            const response = await fetch(`/api/genai/jobs/${jobId}/download-pdf/`, {
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || t('myBooks.downloadFailed'));
                setDownloadingJobId(null);
                return;
            }

            // Create blob from response and trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
            link.download = filenameMatch ? filenameMatch[1] : `book_${jobId}.pdf`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert(t('myBooks.downloadFailed'));
        } finally {
            setDownloadingJobId(null);
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{t('myBooks.title')}</h1>
                <Button onClick={() => navigate('/genai/generate')}>
                    ✨ {t('myBooks.generateNew')}
                </Button>
            </div>

            {loading ? (
                <Loader />
            ) : error ? (
                <Message variant='danger'>{error}</Message>
            ) : jobs && jobs.length === 0 ? (
                <Card className="text-center p-5">
                    <Card.Body>
                        <h3>{t('myBooks.noBooks')}</h3>
                        <p className="text-muted">
                            {t('myBooks.noBooksDescription')}
                        </p>
                        <Button onClick={() => navigate('/genai/generate')}>
                            ✨ {t('myBooks.createFirst')}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <Row xs={1} sm={2} lg={3} className='g-4'>
                    {jobs.map((job) => {
                        const isWaiting = WAITING_STEPS.includes(job.current_step);
                        const isCompleted = job.status === 'completed';
                        const isFailed = job.status === 'failed';
                        return (
                            <Col key={job.job_id}>
                                <Card
                                    className={`h-100 shadow-sm ${
                                        isWaiting ? 'border-warning' :
                                        isCompleted ? 'border-success' :
                                        isFailed ? 'border-danger' : ''
                                    }`}
                                >
                                    <Card.Header className='d-flex justify-content-between align-items-center'>
                                        <BookStatusBadge job={job} />
                                        <small className='text-muted'>{formatDate(job.created_at)}</small>
                                    </Card.Header>
                                    <Card.Body>
                                        <Card.Title className='mb-1'>
                                            {job.input_data?.characters?.[0]?.name || t('myBooks.unnamed')}
                                        </Card.Title>
                                        {job.input_data?.story_freetext && (
                                            <Card.Text className='text-muted small mb-2'>
                                                {job.input_data.story_freetext.length > 60
                                                    ? job.input_data.story_freetext.substring(0, 60) + '…'
                                                    : job.input_data.story_freetext}
                                            </Card.Text>
                                        )}
                                        {isWaiting && (
                                            <span className='badge rounded-pill mt-1' style={{ background: '#fff3cd', color: '#856404', fontSize: '0.75rem' }}>
                                                ✏️ {t('myBooks.needsReview')}
                                            </span>
                                        )}
                                        {isFailed && (
                                            <div className='text-danger small'>
                                                ⚠️ {job.error_message || t('myBooks.generationFailed')}
                                            </div>
                                        )}
                                    </Card.Body>
                                    <Card.Footer className='d-flex gap-2'>
                                        {getActionButton(job)}
                                        {isCompleted && job.pdf_url && (
                                            <Button
                                                variant='outline-secondary'
                                                size='sm'
                                                onClick={() => handleDownloadPDF(job.job_id)}
                                                disabled={downloadingJobId === job.job_id}
                                            >
                                                {downloadingJobId === job.job_id ? t('myBooks.downloading') : `📥 ${t('myBooks.downloadPdf')}`}
                                            </Button>
                                        )}
                                        {isCompleted && (
                                            job.order_is_delivered ? (
                                                <Button
                                                    variant='outline-primary'
                                                    size='sm'
                                                    onClick={() => handleOrderPrint(job)}
                                                    disabled={reorderJobId === job.job_id}
                                                >
                                                    {reorderJobId === job.job_id ? 'Processing...' : '📦 Buy More Copies'}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant='outline-secondary'
                                                    size='sm'
                                                    disabled
                                                    title='Available after your first order is marked delivered'
                                                >
                                                    📦 Buy More Copies
                                                </Button>
                                            )
                                        )}
                                    </Card.Footer>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
        </div>
    );
}

export default MyGeneratedBooksScreen;
